import { captureMessage } from '@sentry/react-native'
import getEnvironment from 'app/environment'
import * as Application from 'expo-application'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Platform } from 'react-native'
import FreshChat from 'shared/wrappers/react-native-freshchat-sdk'
import { useUser } from 'users/queries/detail'
import { useUpdateUserRestoreId } from 'users/queries/update_restore_id'

type FreshchatContextValue = {
  showConversations(): void
  showConversationsAndFAQs(): void
}

export const FreshchatContext = createContext({} as FreshchatContextValue)

interface Props {
  children: React.ReactNode
}

export function FreshchatProvider(props: Props) {
  const updateUserRestoreIdMutation = useUpdateUserRestoreId()
  const { data: user } = useUser()

  const {
    FRESHCHAT_MANAGER_APP_ID,
    FRESHCHAT_MANAGER_APP_KEY,
    FRESHCHAT_HELPER_APP_ID,
    FRESHCHAT_HELPER_APP_KEY,
  } = getEnvironment()
  const [isFreshchatInitialized, setIsFreshchatInitialized] = useState(false)

  const userRole =
    user?.role === 'helper'
      ? 'Helper'
      : user?.role === 'manager'
      ? 'Manager'
      : user?.role === 'observer'
      ? 'Observer'
      : user?.role === 'admin'
      ? 'Admin'
      : 'Uknown Role'
  const appVersion = Application.nativeApplicationVersion

  const initializeFreshchat = useCallback(() => {
    if (Platform.OS !== 'web' && user !== undefined) {
      const freshchatConfig = new FreshChat.FreshchatConfig(
        user.role === 'helper'
          ? FRESHCHAT_HELPER_APP_ID
          : FRESHCHAT_MANAGER_APP_ID,
        user.role === 'helper'
          ? FRESHCHAT_HELPER_APP_KEY
          : FRESHCHAT_MANAGER_APP_KEY
      )
      freshchatConfig.domain = 'msdk.freshchat.com'
      freshchatConfig.teamMemberInfoVisible = true
      freshchatConfig.cameraCaptureEnabled = true
      freshchatConfig.gallerySelectionEnabled = true
      freshchatConfig.responseExpectationEnabled = true
      freshchatConfig.showNotificationBanner = true //iOS only
      freshchatConfig.notificationSoundEnabled = true //iOS only
      freshchatConfig.themeName = 'CustomTheme.plist' //iOS only
      freshchatConfig.stringsBundle = 'FCCustomLocalizable' //iOS only
      FreshChat.Freshchat.init(freshchatConfig)

      setIsFreshchatInitialized(true)
    }
  }, [
    FRESHCHAT_HELPER_APP_ID,
    FRESHCHAT_HELPER_APP_KEY,
    FRESHCHAT_MANAGER_APP_ID,
    FRESHCHAT_MANAGER_APP_KEY,
    user,
  ])

  const initializeFreshchatUser = useCallback(() => {
    if (user !== undefined) {
      const freshchatUser = new FreshChat.FreshchatUser()
      freshchatUser.firstName = user?.firstName
      freshchatUser.lastName = user?.lastName
      freshchatUser.phone = user?.phoneNumber

      FreshChat.Freshchat.setUser(freshchatUser, (userError: any) => {
        console.error(userError)
        captureMessage(userError, 'error')
      })

      const userProperties = {
        cf_next_id: user.id,
        cf_home_store_id: user.homeStoreId?.toString(),
        cf_role: userRole,
        cf_company: user.company,
        cf_next_connect: user.nextConnect,
        cf_app_version: appVersion,
      }

      FreshChat.Freshchat.setUserProperties(userProperties, (error: any) => {
        console.error(error)
        captureMessage(error, 'error')
      })

      FreshChat.Freshchat.identifyUser(
        user.id,
        user.restoreId,
        (error: any) => {
          console.error(error)
          captureMessage(error, 'error')
        }
      )
    }
  }, [appVersion, user, userRole])

  useEffect(() => {
    if (isFreshchatInitialized) {
      initializeFreshchatUser()
    } else {
      initializeFreshchat()
    }
  }, [initializeFreshchat, initializeFreshchatUser, isFreshchatInitialized])

  useEffect(() => {
    if (Platform.OS !== 'web') {
      FreshChat.Freshchat.addEventListener(
        FreshChat.Freshchat.EVENT_USER_RESTORE_ID_GENERATED,
        () => {
          FreshChat.Freshchat.getUser(
            async (freshChatUser: { restoreId: any; externalId: any }) => {
              const restoreId = freshChatUser.restoreId
              if (restoreId) {
                await updateUserRestoreIdMutation.mutateAsync({ restoreId })
              }
            }
          )
          FreshChat.Freshchat.removeEventListeners(
            FreshChat.Freshchat.EVENT_USER_RESTORE_ID_GENERATED
          )
        }
      )
    }
  }, [updateUserRestoreIdMutation])

  const showConversationsAndFAQs = () => {
    FreshChat.Freshchat.showFAQs();
  };

  return (
    <FreshchatContext.Provider
      value={{
        showConversations: FreshChat.Freshchat.showConversations,
        showConversationsAndFAQs,
      }}
    >
      {props.children}
    </FreshchatContext.Provider>
  )
}

export function useFreshchatContext() {
  return useContext<FreshchatContextValue>(FreshchatContext)
}
